import React, { useEffect, useState } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import Loader from "../../../components/loader/loader";
import BreadCrumpBack from "../../../components/navigator/back";
import Snackbar from "../../../components/snackbar/snackbar";
import Swal from "sweetalert2";
import { internshipRequestDetail, getInternshipRankings, selectInternshipApplicant, internshipRequestsList, evaluateInternshipProposal, absorbInternshipProposal } from "../user.api";

// Helper to navigate in functional components
const navigateTo = (path) => {
  window.location.href = path;
};

export default function InternshipProposalDetail() {
  const { id } = useParams();
  const location = useLocation();
  const history = useHistory();
  const query = new URLSearchParams(location.search);
  const isPublicRoute = location.pathname.startsWith("/public");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(query.get("tab") || "details");
  const [rankingsData, setRankingsData] = useState(null);
  const [rankingsLoading, setRankingsLoading] = useState(false);
  const [selectedApplicants, setSelectedApplicants] = useState(new Set());
  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [rankingsError, setRankingsError] = useState(null);
  const servInstance = process.env.REACT_APP_BASE_URL || "";
  let result = servInstance ? servInstance.substring(0, servInstance?.length - 4) : "";

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("tab", tabName);
    history.push({
      pathname: location.pathname,
      search: searchParams.toString()
    });
  };

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    fetchProposalDetail();
  }, [id]);

  useEffect(() => {
    if (activeTab === "applications" && id) {
      // Always refresh rankings data when switching to applications tab
      // This ensures data stays in sync with server state
      fetchRankingsData(true);
    }
  }, [activeTab, id]);

  const fetchRankingsData = (preserveManualSelections = false) => {
    if (!id) return;

    setRankingsLoading(true);
    setRankingsError(null);
    console.log("Fetching rankings data for ID:", id);

    getInternshipRankings(id)
      .then((res) => {
        console.log("Rankings API Response:", res);

        if (res?.success && res?.data) {
          setRankingsData(res.data);

          // Always populate selected applicants based on API response and local state
          const selected = new Set();
          if (res.data.ranked_applicants) {
            res.data.ranked_applicants.forEach(applicant => {

              // Check if applicant is selected based on proposal status
              const isSelected =
                applicant.proposal?.status === 'confirmed' ||
                applicant.proposal?.status === 'company_selected' ||
                applicant.proposal?.status === 'selected' ||
                applicant.proposal?.status === 'allotted' ||
                applicant.proposal?.status === 'doing' ||
                applicant.proposal?.status === 'evaluation' ||
                applicant.proposal?.status === 'evaluated';

              console.log("Is selected:", isSelected, "for status:", applicant.proposal?.status); // Debug selection logic

              if (isSelected) {
                selected.add(applicant.proposal?.id || applicant.application_id || applicant.internship.id);
                console.log("Found selected applicant:", applicant.proposal?.id || applicant.application_id || applicant.internship.id);
              }
            });
          }

          // Preserve manually selected applicants if flag is set
          if (preserveManualSelections) {
            selectedApplicants.forEach(id => selected.add(id));
          }

          setSelectedApplicants(selected);
          console.log("Selected applicants set:", Array.from(selected));

          console.log("Rankings data loaded successfully");
        } else {
          console.error(
            res?.data?.message || "Failed to fetch rankings data",
          );
          setRankingsData(null);
          // Don't clear selections if we're preserving manual selections
          if (!preserveManualSelections) {
            setSelectedApplicants(new Set());
          }
        }
      })
      .catch((err) => {
        console.error("Error fetching rankings data:", err);
        const errorMessage = err.response?.data?.message || "Failed to fetch rankings data";
        setRankingsError(errorMessage);
        setRankingsData(null);
        if (!preserveManualSelections) {
          setSelectedApplicants(new Set());
        }
      })
      .finally(() => {
        setRankingsLoading(false);
      });
  };

  const fetchProposalDetail = () => {
    if (!id) return;

    setLoading(true);
    console.log("Fetching proposal detail for ID:", id);

    internshipRequestDetail(id, isPublicRoute)
      .then((res) => {
        console.log("API Response:", res);

        if (res?.success && res?.data) {
          setData(res.data);
          console.log("Proposal details loaded successfully");
        } else {
          console.error(
            res?.data?.message || "Failed to fetch proposal details",
          );
          setData(null);
        }
      })
      .catch((err) => {
        console.error("Error fetching proposal details:", err);
        setData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleEditClick = () => {
    // Navigate to form with edit mode enabled
    navigateTo(`/internship-proposals-form?edit=${id}`);
  };

  const handleSelectApplicant = (applicant) => {
    const applicantId = applicant.proposal?.id || applicant.application_id || applicant.id;
    const proposalId = applicant.proposal?.id;
    const studentName = applicant.internship?.full_name || "this student";

    if (applicant?.already_taken_by_other_company) {
      setSnackMessage(
        applicant?.taken_elsewhere_message ||
        "This applicant is already allotted in another internship request."
      );
      setIsSnackError(true);
      setSnackOpen(true);
      return;
    }

    // Check if applicant is already selected based on local state only
    if (selectedApplicants.has(applicantId)) {
      return; // Already selected, don't proceed
    }

    Swal.fire({
      title: "Select Application",
      text: `Are you sure you want to select ${studentName} for this internship?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#007bff",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Select",
      cancelButtonText: "Cancel"
    }).then((result) => {
      if (result.isConfirmed) {
        // Add to local state immediately for instant UI feedback
        setSelectedApplicants(prev => new Set(prev).add(applicantId));

        selectInternshipApplicant(proposalId)
          .then((res) => {
            if (res?.success) {
              setSnackMessage("Applicant selected successfully!");
              setIsSnackError(false);
              setSnackOpen(true);
              console.log("Applicant selected successfully");

              // Refresh rankings data to get updated status from server
              setTimeout(() => {
                fetchRankingsData(true);
              }, 1000);

              // Also refresh the main listings data
              internshipRequestsList(isPublicRoute)
                .then((res) => {
                  console.log("Internship listings refreshed successfully");
                })
                .catch((err) => {
                  console.error("Error refreshing internship listings:", err);
                });
            } else {
              // Remove from local state if API call failed
              setSelectedApplicants(prev => {
                const newSet = new Set(prev);
                newSet.delete(applicantId);
                return newSet;
              });
              setSnackMessage(res?.message || "Failed to select applicant");
              setIsSnackError(true);
              setSnackOpen(true);
              console.error("Failed to select applicant");
            }
          })
          .catch((err) => {
            // Remove from local state if API call failed
            setSelectedApplicants(prev => {
              const newSet = new Set(prev);
              newSet.delete(applicantId);
              return newSet;
            });
            setSnackMessage("Error selecting applicant. Please try again.");
            setIsSnackError(true);
            setSnackOpen(true);
            console.error("Error selecting applicant:", err);
          });
      }
    });
  };

  const handleAbsorbApplicant = (applicant) => {
    const proposalId = applicant.proposal?.id;
    const studentName = applicant.internship?.full_name || "this student";

    Swal.fire({
      title: "Absorb Student",
      text: `Confirm that you absorb ${studentName} to the company.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#d33",
      confirmButtonText: "Confirm Absorption",
      cancelButtonText: "Cancel"
    }).then((result) => {
      if (result.isConfirmed) {
        setIsUpdating(true);
        absorbInternshipProposal(proposalId)
          .then((res) => {
            if (res?.success) {
              setSnackMessage("Student absorbed successfully!");
              setIsSnackError(false);
              setSnackOpen(true);
              fetchRankingsData(true);
            } else {
              setSnackMessage(res?.message || "Failed to absorb student");
              setIsSnackError(true);
              setSnackOpen(true);
            }
          })
          .catch((err) => {
            setSnackMessage("Error absorbing student.");
            setIsSnackError(true);
            setSnackOpen(true);
          })
          .finally(() => setIsUpdating(false));
      }
    });
  };

  const handleEvaluateApplicant = (proposalId) => {
    if (!proposalId) {
      setSnackMessage("Proposal ID is missing");
      setIsSnackError(true);
      setSnackOpen(true);
      return;
    }

    const today = new Date().toISOString().split('T')[0];

    Swal.fire({
      title: "Evaluate Applicant",
      html: `
        <div style="text-align: left;">
          <div class="mb-3 text-sm">
            <label for="evaluate-start-date" class="form-label">Start Date <span class="text-danger">*</span></label>
            <input type="date" id="evaluate-start-date" class="form-control" max="${today}" required>
          </div>
          <div class="mb-3 text-sm">
            <label for="evaluate-date" class="form-label">Completion Date <span class="text-danger">*</span></label>
            <input type="date" id="evaluate-date" class="form-control" required>
          </div>
          <div class="mb-3 text-sm">
            <label for="evaluate-remarks" class="form-label">Evaluation Remarks <span class="text-danger">*</span></label>
            <textarea id="evaluate-remarks" class="form-control" rows="3" required></textarea>
          </div>
          <div class="mb-3 text-sm">
            <label for="evaluate-file" class="form-label">Evaluation Report File <span class="text-danger">*</span></label>
            <input type="file" id="evaluate-file" class="form-control" accept=".pdf,.doc,.docx" required>
            <small class="text-muted d-block mt-1">Maximum file size: 5MB</small>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      confirmButtonText: "Evaluate",
      cancelButtonText: "Cancel",
      didOpen: () => {
        const startDateInput = document.getElementById('evaluate-start-date');
        const completionDateInput = document.getElementById('evaluate-date');
        startDateInput.addEventListener('change', () => {
          if (startDateInput.value) {
            completionDateInput.min = startDateInput.value;
            if (completionDateInput.value && completionDateInput.value < startDateInput.value) {
              completionDateInput.value = startDateInput.value;
            }
          }
        });
      },
      preConfirm: () => {
        const startDate = document.getElementById('evaluate-start-date').value;
        const date = document.getElementById('evaluate-date').value;
        const remarks = document.getElementById('evaluate-remarks').value;
        const file = document.getElementById('evaluate-file').files[0];

        if (!startDate || !date || !remarks || !file) {
          Swal.showValidationMessage('Please fill all fields and select a file');
          return false;
        }

        if (date < startDate) {
          Swal.showValidationMessage('Completion date cannot be earlier than start date');
          return false;
        }

        if (file.size > 5 * 1024 * 1024) {
          Swal.showValidationMessage('File size exceeds 5MB limit');
          return false;
        }

        return { startDate, date, remarks, file };
      }
    }).then((result) => {
      if (result.isConfirmed) {
        const { startDate, date, remarks, file } = result.value;

        // Create FormData
        const formData = new FormData();
        formData.append('start_date', startDate);
        formData.append('completion_date', date);
        formData.append('evaluation_remarks', remarks);
        formData.append('evaluation_report_file', file);

        setIsUpdating(true);

        // Call the API with FormData
        evaluateInternshipProposal(proposalId, formData)
          .then((res) => {
            if (res?.success) {
              setSnackMessage(res?.message || "Applicant evaluated successfully");
              setIsSnackError(false);
              setSnackOpen(true);
              // Refresh the rankings data to get updated status
              fetchRankingsData(true);
            } else {
              setSnackMessage(res?.message || "Failed to evaluate applicant");
              setIsSnackError(true);
              setSnackOpen(true);
            }
            setIsUpdating(false);
          })
          .catch((error) => {
            console.error("Evaluation error:", error);
            const errorData = error.response?.data?.error;
            let errorMessage = "Error evaluating applicant";

            if (errorData) {
              errorMessage = Object.values(errorData).flat().join(', ');
            } else if (error.message) {
              errorMessage = error.message;
            }

            setSnackMessage(errorMessage);
            setIsSnackError(true);
            setSnackOpen(true);
            setIsUpdating(false);
          });
      }
    });
  };

  const snackbarClose = () => {
    setSnackMessage("");
    setIsSnackError(false);
    setSnackOpen(false);
  };

  if (loading) return <Loader open={true} />;

  if (!data)
    return (
      <div className="container mt-5">
        <BreadCrumpBack />
        <div className="alert alert-warning mt-3">
          <p>
            No data found. The proposal may not exist or you don't have
            permission to view it.
          </p>
          <p className="text-muted small">
            Check the browser console for error details.
          </p>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => window.history.back()}
          >
            Go Back
          </button>
        </div>
      </div>
    );

  // Helper to format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString();
  };

  // Helper to get qualification names
  const getQualificationNames = (ids) => {
    if (!Array.isArray(ids) || !data?.all_qualifications) return "";

    // Convert object to array if needed (API returns object with string keys)
    const qualificationsArray = Array.isArray(data.all_qualifications)
      ? data.all_qualifications
      : Object.values(data.all_qualifications);

    return qualificationsArray
      .filter((q) => ids.includes(q.id))
      .map((q) => q.name)
      .join(", ");
  };

  // Helper to get status badge color
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "approved":
        return "success";
      case "rejected":
      case "initially_rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "pending":
        return "Pending";
      case "approved":
        return "Approved";
      case "rejected":
        return "Rejected";
      case "initially_rejected":
        return "Rejected";
      default:
        return status ? status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ") : "Unknown";
    }
  };

  // Helper to get application status badge info (color, icon, text)
  const getApplicationStatusInfo = (status) => {
    const rawStatus = (status || "pending").toLowerCase();

    // Default text formatting (replace underscores with spaces, capitalize)
    const formatText = (str) => {
      if (!str) return "Unknown";
      return str.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    let style = { bg: "#e2e3e5", color: "#383d41" }; // Default
    let icon = "fa fa-info-circle";

    switch (rawStatus) {
      case "pending":
      case "open":
        style = { bg: "#fff3cd", color: "#856404" }; // Yellow
        icon = "fa fa-hourglass-half";
        break;
      case "confirmed":
        style = { bg: "#d4edda", color: "#155724" }; // Green
        icon = "fa fa-check-circle";
        break;
      case "ranking_confirmed":
        style = { bg: "#c3e6cb", color: "#155724" }; // Darker Green
        icon = "fa fa-check-circle";
        break;
      case "company_selected":
        style = { bg: "#cce5ff", color: "#004085" }; // Blue
        icon = "fa fa-building";
        break;
      case "selected":
        style = { bg: "#d1ecf1", color: "#0c5460" }; // Cyan
        icon = "fa fa-check";
        break;
      case "allotted":
        style = { bg: "#e0f2fe", color: "#0369a1" }; // Sky Blue
        icon = "fa fa-tasks";
        break;
      case "doing":
        style = { bg: "#fad7a1", color: "#7e5109" }; // Orange
        icon = "fa fa-spinner fa-spin";
        break;
      case "evaluation":
        style = { bg: "#e8daef", color: "#4a235a" }; // Purple
        icon = "fa fa-file-text-o";
        break;
      case "evaluated":
        style = { bg: "#d5f5e3", color: "#145a32" }; // Mint
        icon = "fa fa-graduation-cap";
        break;
      case "certified":
        style = { bg: "#aed6f1", color: "#1b4f72" }; // Sky Blue
        icon = "fa fa-certificate";
        break;
      case "absorbed":
        style = { bg: "#a9dfbf", color: "#145a32" }; // Soft Green
        icon = "fa fa-user-plus";
        break;
      case "rejected":
        style = { bg: "#f2d7d5", color: "#78281f" }; // Red
        icon = "fa fa-times-circle";
        break;
      default:
        break;
    }

    return { style, icon, text: formatText(rawStatus) };
  };

  // Helper to format CGPA
  const formatCGPA = (cgpa) => {
    if (cgpa === null || cgpa === undefined) return "N/A";
    return cgpa.toFixed(1);
  };

  return (
    <div className="detail-page">
      <Loader open={isUpdating} />
      <Snackbar
        message={snackMessage}
        error={isSnackError}
        open={snackOpen}
        close={snackbarClose}
      />
      <div className="container mt-5 mb-5">
        <BreadCrumpBack />

        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="mt-4">Internship Proposal Details</h3>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "details" ? "active" : ""}`}
              onClick={() => handleTabChange("details")}
            >
              Details
            </button>
          </li>
          {!isPublicRoute && (
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "applications" ? "active" : ""}`}
                onClick={() => handleTabChange("applications")}
              >
                Application
              </button>
            </li>
          )}
        </ul>

        {/* Tab Content */}
        {activeTab === "details" && (
          <div className="card p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                {/* <span className={`badge bg-${getStatusBadgeClass(data.status)} px-3 py-2`} style={{ borderRadius: '20px', fontSize: '12px' }}>
                  <i className="fa fa-info-circle me-1"></i> {getStatusText(data.status)}
                </span> */}
              </div>
              <div className="d-flex gap-2">
                {/* {isPublicRoute && (
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => {
                      // Save intended destination before navigating
                      // This ensures the login flow redirects back here after auth
                      sessionStorage.setItem("next_path", "/training-reg");
                      sessionStorage.setItem("active_path", "/training-reg");
                      history.push("/training-reg");
                    }}
                  >
                    <i className="fa fa-paper-plane me-1"></i> APPLY NOW
                  </button>
                )} */}
                {!isPublicRoute && data?.ranking_status === "open" && (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleEditClick}
                  >
                    Edit Proposal
                  </button>
                )}
              </div>
            </div>

            <div className="company-header d-flex align-items-center mb-4 p-3 bg-light rounded">
              {data.company?.logo ? (
                <img
                  src={result + data.company.logo}
                  alt="Company Logo"
                  className="me-3"
                  style={{ width: '80px', height: '80px', objectFit: 'contain' }}
                />
              ) : (
                <div className="company-placeholder me-3 d-flex align-items-center justify-content-center bg-white border" style={{ width: '80px', height: '80px', borderRadius: '8px' }}>
                  <i className="fa fa-building fa-2x text-muted"></i>
                </div>
              )}
              <div>
                <h4 className="mb-1 text-primary">{data.company?.company_name}</h4>
                <p className="mb-0 text-muted small">{data.company?.address}</p>
                {data.company?.site_link && (
                  <a href={data.company.site_link} target="_blank" rel="noopener noreferrer" className="small">
                    {data.company.site_link} <i className="fa fa-external-link"></i>
                  </a>
                )}
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-3 font-weight-bold">Discipline:</div>
              <div className="col-md-9">{data.internship_discipline}</div>
            </div>

            <div className="row mb-3">
              <div className="col-md-3 font-weight-bold">Brief Description:</div>
              <div className="col-md-9">{data.brief_description}</div>
            </div>

            <div className="row mb-5">
              <div className="col-md-12">
                <div className="d-flex flex-wrap gap-4 mb-4">
                  <div className="course-status-box">
                    <div className="text-muted small mb-2 font-weight-bold" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Course Completion Status</div>
                    <div className="d-flex gap-4">
                      <span className="small d-flex align-items-center text-dark">
                        {(data.target_category === 'students_undergoing_course' || data.target_category === 'both') ? (
                          <i className="fa fa-check-circle text-success me-2" style={{ fontSize: '16px' }}></i>
                        ) : (
                          <i className="fa fa-times-circle text-danger me-2" style={{ fontSize: '16px' }}></i>
                        )}
                        Undergoing
                      </span>
                      <span className="small d-flex align-items-center text-dark">
                        {(data.target_category === 'students_completed_course' || data.target_category === 'both') ? (
                          <i className="fa fa-check-circle text-success me-2" style={{ fontSize: '16px' }}></i>
                        ) : (
                          <i className="fa fa-times-circle text-danger me-2" style={{ fontSize: '16px' }}></i>
                        )}
                        Completed
                      </span>
                    </div>
                  </div>

                  <div className="positions-box">
                    <div className="text-muted small mb-2 font-weight-bold" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Capacity</div>
                    <span className="badge bg-white text-dark border px-3 py-2" style={{ borderRadius: '20px', fontSize: '12px', fontWeight: '500', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                      <i className="fa fa-user-circle me-2 text-primary"></i>
                      {data.number_of_students || "0"} Positions Available
                    </span>
                  </div>
                </div>

                <div className="mb-4" style={{ maxWidth: '400px' }}>
                  <div className="d-flex align-items-center bg-light p-3 rounded" style={{ fontSize: '13px', border: '1px dashed #dee2e6' }}>
                    <i className="fa fa-calendar-check-o me-3 text-primary" style={{ fontSize: '24px' }}></i>
                    <div>
                      <div className="text-muted mb-1" style={{ fontSize: '10px', textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: '0.5px' }}>Internship Period</div>
                      <div className="font-weight-bold text-dark" style={{ fontSize: '15px' }}>
                        {formatDate(data.preferred_start_date)} - {formatDate(data.preferred_end_date)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-3 font-weight-bold">
                Additional Certifications:
              </div>
              <div className="col-md-9 text-muted">
                {data.additional_certifications || "N/A"}
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-3 font-weight-bold">Grade Criteria:</div>
              <div className="col-md-9 text-muted">{data.grade_criteria || "N/A"}</div>
            </div>

            <hr className="my-4" />

            <h5 className="mt-4 mb-3">Required Qualifications</h5>

            <div className="row mb-3">
              <div className="col-md-3 font-weight-bold">Qualification Type:</div>
              <div className="col-md-9">{data.qualification_type?.name}</div>
            </div>

            <div className="row mb-3">
              <div className="col-md-3 font-weight-bold">
                Qualifications:
              </div>
              <div className="col-md-9">
                {getQualificationNames(data.qualification_type_parents) || "N/A"}
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-3 font-weight-bold">
                Streams:
              </div>
              <div className="col-md-9">
                {getQualificationNames(data.qualification_type_subs) || "N/A"}
              </div>
            </div>
            <div className="row mb-12 justify-content-center">
              {isPublicRoute && (
                <div className="col-auto ">
                  <button
                    className="btn btn-success btn-sm w-auto d-inline-flex align-items-center "
                    onClick={() => {
                      // Save intended destination before navigating
                      // This ensures the login flow redirects back here after auth
                      sessionStorage.setItem("next_path", "/training-reg");
                      sessionStorage.setItem("active_path", "/training-reg");
                      history.push("/training-reg");
                    }}
                  >
                    <i className="fa fa-paper-plane me-2 "></i> APPLY NOW
                  </button>
                </div>
              )}
            </div>

          </div>
        )}

        {activeTab === "applications" && (
          <div className="card-body p-0">
            {rankingsLoading ? (
              <div className="p-5 text-center">
                <Loader open={true} />
              </div>
            ) : rankingsData?.ranked_applicants?.length > 0 ? (
              <div className="p-4">
                <div className="ranking-status-card mb-4 d-flex align-items-center justify-content-between">
                  <div>
                    <h5 className="mb-1 fw-bold">Application Rankings</h5>
                    {/* <p className="text-muted small mb-0">List of applicants sorted by their system-generated/admin ranks</p> */}
                  </div>
                  <div className="text-end">
                    <div className="text-muted small fw-bold text-uppercase mb-1" style={{ letterSpacing: '0.5px', fontSize: '10px' }}>Current Ranking Status</div>
                    {(() => {
                      const statusInfo = getApplicationStatusInfo(rankingsData.ranking_status);
                      return (
                        <span className="status-pill badge" style={{ backgroundColor: statusInfo.style.bg, color: statusInfo.style.color, border: `1px solid ${statusInfo.style.color}` }}>
                          <i className={`${statusInfo.icon} me-1`}></i> {statusInfo.text}
                        </span>
                      );
                    })()}
                  </div>
                </div>

                <div className="rankings-table-container">
                  <div className="table-responsive">
                    <table className="table rankings-table">
                      <thead>
                        <tr>
                          <th style={{ width: '80px' }} className="text-center">Rank</th>
                          <th>Applicant Details</th>
                          <th className="text-center">Academic Profile</th>
                          <th className="text-center">Preference</th>
                          <th className="text-center">Status</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rankingsData.ranked_applicants.map((applicant, index) => (
                          <tr
                            key={index}
                            className={applicant?.already_taken_by_other_company ? "already-taken-row" : ""}
                            title={
                              applicant?.already_taken_by_other_company
                                ? `${applicant?.taken_elsewhere_message || "This applicant is already allotted in another internship request."}${applicant?.taken_elsewhere_details?.company_name
                                  ? ` Current company: ${applicant.taken_elsewhere_details.company_name}`
                                  : ""
                                }`
                                : ""
                            }
                          >
                            <td className="text-center">
                              <div className="d-flex justify-content-center">
                                <div className="rank-circle">
                                  {applicant.effective_rank}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="applicant-meta text-start">
                                <div className="applicant-name-text">{applicant.internship.full_name}</div>
                                <div className="applicant-sub-text">
                                  <i className="fa fa-envelope me-1"></i> {applicant.internship.email}
                                </div>
                                <div className="applicant-sub-text">
                                  <i className="fa fa-phone fa-flip-horizontal me-1"></i> {applicant.internship.phone_no}
                                </div>
                              </div>
                            </td>
                            <td className="text-center">
                              <div className="d-flex flex-column align-items-center gap-2">
                                <div className="cgpa-vibrant" title="CGPA">
                                  <span className="small text-muted fw-normal me-1">CGPA:</span>
                                  {formatCGPA(applicant.latest_cgpa)}
                                </div>
                                <div className="score-vibrant" title="Score">
                                  <span className="small text-muted fw-normal me-1">Score:</span>
                                  {applicant.latest_score || "N/A"}
                                </div>
                              </div>
                            </td>
                            <td className="text-center">
                              <div className="fw-bold text-primary">Choice #{applicant.preference_order}</div>
                            </td>
                            <td className="text-center">
                              {(() => {
                                const statusInfo = getApplicationStatusInfo(applicant.proposal?.status);
                                return (
                                  <span className="status-pill badge" style={{ backgroundColor: statusInfo.style.bg, color: statusInfo.style.color, border: `1px solid ${statusInfo.style.color}` }}>
                                    <i className={`${statusInfo.icon} me-1`}></i> {statusInfo.text}
                                  </span>
                                );
                              })()}
                            </td>
                            <td>
                              <div className="d-flex gap-2 justify-content-start">
                                <button
                                  className="btn btn-outline-info table-action-btn"
                                  onClick={() => window.open(`/internship-proposals/${id}/applicant/${applicant.proposal?.id}`, "_blank")}
                                  title="View Full Profile"
                                >
                                  <i className="fa fa-eye me-1"></i> Profile
                                </button>
                                {applicant.proposal?.status === 'evaluation' ? (
                                  <button
                                    className="btn btn-success table-action-btn"
                                    onClick={() => handleEvaluateApplicant(applicant.proposal?.id)}
                                    disabled={isUpdating || applicant?.already_taken_by_other_company}
                                  >
                                    <i className="fa fa-check-circle me-1"></i> Evaluate
                                  </button>
                                ) : applicant.proposal?.status === 'certified' ? (
                                  <button
                                    className="btn btn-warning table-action-btn"
                                    onClick={() => handleAbsorbApplicant(applicant)}
                                    disabled={isUpdating || applicant?.already_taken_by_other_company}
                                  >
                                    <i className="fa fa-user-plus me-1"></i> Absorb
                                  </button>
                                ) : applicant.proposal?.status === 'absorbed' ? (
                                  <span className="badge bg-success-light text-success p-2">
                                    <i className="fa fa-check-circle me-1"></i> Absorbed
                                  </span>
                                ) : applicant?.already_taken_by_other_company ? (
                                  <></>
                                ) : (
                                  <button
                                    className={`btn table-action-btn ${selectedApplicants.has(applicant.proposal?.id || applicant.application_id || applicant.internship.id) ? 'btn-success' : 'btn-primary'}`}
                                    onClick={() => handleSelectApplicant(applicant)}
                                    disabled={selectedApplicants.has(applicant.proposal?.id || applicant.application_id || applicant.internship.id)}
                                  >
                                    {selectedApplicants.has(applicant.proposal?.id || applicant.application_id || applicant.internship.id) ? (
                                      <><i className="fa fa-check me-1"></i> Selected</>
                                    ) : (
                                      <><i className="fa fa-plus-circle me-1"></i> Select</>
                                    )}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : rankingsError ? (
              <div className="p-5">
                <div className="alert alert-warning mb-0">
                  <i className="fa fa-info-circle me-2"></i>
                  {rankingsError}
                </div>
              </div>
            ) : (
              <div className="p-5">
                <div className="alert alert-info mb-0">
                  <i className="fa fa-users me-2"></i>
                  No application data available for this internship proposal.
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
